import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import authService from "@/services/authService";
import { API_BASE_URL } from "@/services/apiConfig";
import { toast } from "sonner";
import { ArrowLeft, BookOpen, Eye, EyeOff, LockKeyhole, Mail } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const EMERGENT_AUTH_URL = "https://auth.emergentagent.com";
const LOGIN_BACKGROUND = "/images/login-gold-background.png";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleAuthEnabled, setGoogleAuthEnabled] = useState(true);
  const [processingGoogle, setProcessingGoogle] = useState(false);

  // Check if Google auth is enabled and process any session_id from URL
  useEffect(() => {
    checkGoogleAuthEnabled();
    processGoogleCallback();
  }, []);

  const checkGoogleAuthEnabled = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/google/enabled`);
      const data = await response.json();
      setGoogleAuthEnabled(data.enabled);
    } catch (error) {
      console.error("Error checking Google auth:", error);
      setGoogleAuthEnabled(false);
    }
  };

  const processGoogleCallback = async () => {
    // Check URL fragment for session_id
    const hash = window.location.hash;
    if (hash && hash.includes("session_id=")) {
      setProcessingGoogle(true);
      const sessionId = hash.split("session_id=")[1]?.split("&")[0];
      
      if (sessionId) {
        try {
          // Exchange session_id for JWT
          const formData = new FormData();
          formData.append("session_id", sessionId);
          
          const response = await fetch(`${API_BASE_URL}/auth/google/session`, {
            method: "POST",
            body: formData,
          });
          
          if (!response.ok) {
            throw new Error("Failed to process Google login");
          }
          
          const data = await response.json();
          
          // Store tokens
          authService.setTokens(data.access_token, data.refresh_token);
          
          // Clear the hash
          window.history.replaceState(null, "", window.location.pathname);
          
          toast.success(`Welcome, ${data.user.name}!`);
          
          // Navigate based on role
          const role = data.user.role;
          if (role === "admin") {
            navigate("/admin");
          } else if (role === "narrator") {
            navigate("/narrator");
          } else if (role === "publisher") {
            navigate("/publisher/dashboard");
          } else {
            navigate("/dashboard");
          }
        } catch (error) {
          console.error("Google auth error:", error);
          toast.error("Failed to complete Google login");
          window.history.replaceState(null, "", window.location.pathname);
        }
      }
      setProcessingGoogle(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/login";
    window.location.href = `${EMERGENT_AUTH_URL}/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await authService.login(email, password);
      const user = await authService.getMe().catch(() => null);
      toast.success("Login successful!");

      if (user?.role === "admin") {
        navigate("/admin");
      } else if (user?.role === "narrator") {
        navigate("/narrator");
      } else if (user?.role === "publisher") {
        navigate("/publisher/dashboard");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Login error:", error);
      const message = error.response?.data?.detail || "Invalid email or password";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (processingGoogle) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-black text-[#f7ead2]">
        <img
          src={LOGIN_BACKGROUND}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/35" />
        <div className="relative text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-2 border-[#f4c766]/20 border-b-[#f4c766]"></div>
          <p className="mt-4 text-[#f7ead2]/80">Completing Google sign-in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gr-auth relative min-h-screen overflow-hidden bg-black text-[#f7ead2]">
      <style>{`
        .gr-auth input:-webkit-autofill,
        .gr-auth input:-webkit-autofill:hover,
        .gr-auth input:-webkit-autofill:focus {
          -webkit-text-fill-color: #f7ead2;
          caret-color: #f7ead2;
          box-shadow: 0 0 0 1000px rgba(0, 0, 0, 0.42) inset, inset 0 0 16px rgba(0, 0, 0, 0.45);
          transition: background-color 9999s ease-out 0s;
        }
      `}</style>
      <img
        src={LOGIN_BACKGROUND}
        alt=""
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(232,173,49,0.04),rgba(0,0,0,0.28)_48%,rgba(0,0,0,0.7))]" />
      <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black/80 to-transparent" />

      <main className="relative z-10 flex min-h-screen flex-col items-center justify-center px-5 py-8 sm:px-8">
        <section className="relative w-full max-w-[460px] pt-9">
          <div className="absolute left-1/2 top-0 z-20 flex h-[72px] w-[72px] -translate-x-1/2 items-center justify-center rounded-[20px] border border-[#f8ca62]/70 bg-black/55 shadow-[0_0_34px_rgba(232,179,67,0.58),inset_0_0_22px_rgba(232,179,67,0.18)] backdrop-blur-md sm:h-20 sm:w-20">
            <BookOpen className="h-9 w-9 text-[#ffd76d] drop-shadow-[0_0_12px_rgba(255,215,109,0.8)] sm:h-10 sm:w-10" />
          </div>

          <div
            className="rounded-[24px] border border-[#f4c766]/70 bg-black/50 px-6 pb-7 pt-11 shadow-[0_0_48px_rgba(214,158,46,0.34),inset_0_1px_22px_rgba(255,223,137,0.12)] backdrop-blur-xl sm:px-8"
            data-testid="login-card"
          >
            <div className="text-center">
              <h1
                className="text-3xl font-bold leading-tight text-[#f1bd46] sm:text-4xl"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                Welcome Back
              </h1>
              <p className="mt-2 text-sm text-[#f7ead2]/85">
                Sign in to access your dashboard
              </p>
            </div>

            {googleAuthEnabled && (
              <>
                <Button
                  onClick={handleGoogleLogin}
                  variant="outline"
                  className="mt-6 h-12 w-full rounded-lg border border-[#b9831b]/75 bg-black/30 text-base font-semibold text-[#f7ead2] shadow-[inset_0_0_18px_rgba(232,179,67,0.08)] transition-all hover:border-[#ffd76d] hover:bg-[#d99b1f]/10 hover:text-white"
                  data-testid="google-login-button"
                >
                  <span className="mr-3 text-2xl font-bold text-[#f1bd46]">G</span>
                  Continue with Google
                </Button>

                <div className="relative my-5">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-[#f4c766]/25" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-[#070604] px-5 text-[#f4c766]/85">
                      Or continue with email
                    </span>
                  </div>
                </div>
              </>
            )}

            <form
              onSubmit={handleSubmit}
              className="space-y-4"
              data-testid="login-form"
            >
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm text-[#f7ead2]">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-[#f4c766]" />
                  <Input
                    data-testid="login-email-input"
                    id="email"
                    type="email"
                    placeholder="admin@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 rounded-lg border border-[#d1a64d]/45 bg-black/30 pl-12 pr-4 text-sm text-[#f7ead2] placeholder:text-[#d8be86]/60 shadow-[inset_0_0_16px_rgba(0,0,0,0.45)] focus:border-[#ffd76d] focus:ring-2 focus:ring-[#f4c766]/25"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm text-[#f7ead2]">
                  Password
                </Label>
                <div className="relative">
                  <LockKeyhole className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-[#f4c766]" />
                  <Input
                    data-testid="login-password-input"
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 rounded-lg border border-[#d1a64d]/45 bg-black/30 pl-12 pr-12 text-sm text-[#f7ead2] placeholder:text-[#d8be86]/60 shadow-[inset_0_0_16px_rgba(0,0,0,0.45)] focus:border-[#ffd76d] focus:ring-2 focus:ring-[#f4c766]/25"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    className="absolute right-4 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-[#f4c766] transition hover:bg-[#f4c766]/10 hover:text-[#ffd76d]"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                data-testid="login-submit-button"
                type="submit"
                className="h-12 w-full rounded-lg bg-gradient-to-r from-[#a56e0a] via-[#f0c055] to-[#a66b08] text-sm font-bold text-black shadow-[0_0_24px_rgba(234,176,55,0.38),inset_0_1px_0_rgba(255,255,255,0.35)] transition-all duration-300 hover:brightness-110"
                disabled={loading}
              >
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-[#f7ead2]/80">
                Don't have an account?{" "}
                <button
                  data-testid="login-register-link"
                  onClick={() => navigate("/register")}
                  className="font-semibold text-[#f4c766] hover:text-[#ffd76d]"
                >
                  Register
                </button>
              </p>
            </div>
          </div>

          <div className="mt-6 text-center">
            <button
              data-testid="back-to-home-link"
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 text-base font-medium text-[#f4c766] transition hover:text-[#ffd76d]"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to home
            </button>
          </div>
        </section>
      </main>

      <div className="pointer-events-none absolute bottom-8 right-8 hidden items-center gap-4 text-[#dba63a] drop-shadow-[0_0_18px_rgba(219,166,58,0.45)] md:flex">
        <div className="relative h-14 w-14 rounded-full border-2 border-[#dba63a]/80">
          <div className="absolute inset-2 rounded-full border border-[#dba63a]/70" />
          <div className="absolute inset-5 rounded-full bg-[#dba63a]" />
        </div>
        <div className="leading-none tracking-[0.38em]">
          <p className="text-xl font-semibold">GOLDEN</p>
          <p className="mt-2 text-sm">RECORDS</p>
        </div>
      </div>
    </div>
  );
}
